"use client";

/**
 * FormModal for Landing Page V2
 * Renders a form in a modal with optional two-step payment checkout.
 * Flow: Form fields → (if payment enabled) "Next" → CheckoutStep → Submit
 * Used by SmartButton when a CTA links to a form.
 */

import FetchModal from "@/fetch-ui/FetchModal";
import FormFieldRenderer, { HtmlDescription } from "@/features/forms/_components/renderer/FormFieldRenderer";
import { FieldComment } from "@/features/forms/_components/renderer/FieldComment";
import { commentKeyForLabel } from "@/lib/database/forms";
import { computeVisibleFields } from "@/lib/forms/conditional-visibility";
import { FormDescriptionContent } from "@/features/forms/_components/renderer/FormDescriptionContent";
import { FormSaveIndicator } from "@/features/forms/_components/shared/FormSaveIndicator";
import { useFormAutoSave } from "@/features/forms/_hooks/useFormAutoSave";
import { mergeDraftOverFormData } from "@/features/forms/_logic/merge-draft-over-form-data";
import { mergeSavedResponseOverDefaults } from "@/features/forms/_logic/merge-saved-response-over-defaults";
import { FORM_FIELDS_PER_PAGE } from "@/features/landing-page-v2/_logic/constants";
import { invalidateFormCache } from "@/features/landing-page-v2/_logic/form-prefetch";
import {
  EMPTY_CTA_STEP_CONTACT,
  withCarriedContact,
  type CtaStepSequenceContext,
} from "@/features/landing-page-v2/_logic/cta-steps";
import { useFormStepSequence } from "@/features/landing-page-v2/_hooks/useFormStepSequence";
import { prefillFormDataFromViewer } from "@/features/landing-page-v2/_logic/utils/prefill-form-from-viewer";
import { useFormSubmissionState } from "@/features/landing-page-v2/_hooks/useFormSubmissionState";
import { useRenderableForm } from "@/features/landing-page-v2/_hooks/useRenderableForm";
import { useFilledFilePoll } from "@/features/landing-page-v2/_hooks/useFilledFilePoll";
import { useFormSubmitFlow } from "@/features/landing-page-v2/_hooks/useFormSubmitFlow";
import {
  ACCENT_BUTTON_CLASS,
  getAccentButtonStyle,
  getButtonTextColor,
  getReadableTextColor,
} from "@/features/landing-pages/_logic/color-utils";
import { useToast } from "@/components/providers/FetchToastProvider";
import type { FormField } from "@/lib/database/forms";
import { sendFormConfirmationEmail } from "@/lib/forms/send-confirmation-email";
import { useFormFileUpload } from "@/lib/forms/hooks";
import { getDateFieldBounds, initializeFormData, validateFormSubmission } from "@/lib/forms/utilities";
import { getCountryDialCodeById } from "@/lib/db/countries-dial-codes";
import { resolveRespondentTypeForViewer } from "@/features/forms/_logic/actions/respondent-type-actions";
import { useFormPayment } from "@/features/landing-page-v2/_hooks/useFormPayment";
import { createClient } from "@/utils/supabase/client";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import type { OrderContext, FormSummaryEntry } from "./CheckoutStep";

// Dynamic-imported so the Stripe bundle (@stripe/react-stripe-js + the js.stripe.com
// script) only loads for forms that actually have payment enabled — the majority of
// public forms never pay this cost.
const CheckoutStep = dynamic(() => import("./CheckoutStep").then((mod) => mod.CheckoutStep), { ssr: false });
import { FormModalErrorBoundary } from "./FormModalErrorBoundary";
import { FormThankYou } from "./FormThankYou";
import { CtaStepLabel } from "./CtaStepLabel";
import { FormPageIndicator } from "./FormPageIndicator";
import { FormAlreadySubmitted } from "./FormAlreadySubmitted";
import { HtmlLabel } from "./HtmlLabel";
import { usePublicViewContext } from "./PublicViewContext";
import { PUBLIC_MODAL_COMPACT_CLASSNAME, PUBLIC_MODAL_FULL_SCREEN_MOBILE_CLASSNAME } from "./public-modal-classnames";

/**
 * Field types whose control renders its own inline error border (FetchInput / FetchTextArea /
 * FetchDropdown / phone). These rely on that per-control border, so we don't draw the wrapper ring
 * around the whole field group. The remaining types (signature, address, file, ranking, scale) have
 * no inline border, so they keep the wrapper ring as their invalid cue.
 */
const FIELD_TYPES_WITH_INLINE_ERROR_BORDER: ReadonlySet<string> = new Set([
  "text",
  "email",
  "number",
  "url",
  "date",
  "datetime-local",
  "time",
  "tel",
  "dropdown",
  "multiplechoice",
  "multiselect",
]);

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: number;
  eventId: number;
  primaryColor: string;
  /** Optional context about the item being purchased (from pricing card) */
  orderContext?: OrderContext;
  /**
   * Respondent-type invite token carried by the specific button/link that opened
   * this modal (e.g. a pricing card linked to one tier). Takes precedence over the
   * page URL's own `?rt=` — the modal opens in place without navigating, so a
   * button-specific token would otherwise never reach the resolver.
   */
  inviteToken?: string | null;
  /**
   * Set when this form is one step of a multi-step CTA. Adds the "Step X of Y" label, pre-fills the
   * carried contact, and reports completion so the next step can open. Omit for a standalone form.
   */
  sequence?: CtaStepSequenceContext;
  /**
   * Render only the form's content, without its own modal, for a host that supplies the dialog (the
   * multi-step CTA keeps one dialog open across steps). `isOpen`/`onClose` still apply.
   */
  embedded?: boolean;
}

/** Form modal with optional payment checkout step for landing page v2 */
export function FormModal({
  isOpen,
  onClose,
  formId,
  eventId,
  primaryColor,
  orderContext,
  inviteToken: inviteTokenProp,
  sequence,
  embedded = false,
}: FormModalProps) {
  // Hooks
  // Stable client for the component's life so `loadForm` (which lists `supabase` as
  // a dep) isn't rebuilt every render, independent of createClient's singleton-ness.
  const supabase = useMemo(() => createClient(), []);
  const { uploadFile, uploadingFields } = useFormFileUpload(supabase, formId);
  const formScrollRef = useRef<HTMLDivElement | null>(null);
  const hasMountedRef = useRef(false);
  const buttonTextColor = getButtonTextColor(primaryColor);
  const { viewer, landingPageId } = usePublicViewContext();
  const searchParams = useSearchParams();
  const { showToast } = useToast();
  // Identity used only to pre-fill fields: the viewer plus any contact carried from an earlier CTA
  // step. The real `viewer` still drives respondent type and edit rights below.
  const carriedContact = sequence?.prefillContact ?? EMPTY_CTA_STEP_CONTACT;
  const prefillViewer = useMemo(() => withCarriedContact(viewer, carriedContact), [viewer, carriedContact]);

  // States
  // Resolved independently of `viewer` — an anonymous visitor with no verified
  // identity can still resolve via invite token or the form's default bucket.
  const [respondentTypeId, setRespondentTypeId] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form data values are heterogeneous (strings, arrays, objects with phone/countryDialCodeId)
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<{ data: Record<string, unknown>; page: number } | null>(null);

  // Destructured (not held as one object) so dependency arrays below can depend on
  // the individual stable action references instead of a new object every render.
  const {
    phase: submissionPhase,
    recap: submissionRecap,
    markSubmitted,
    showRecap,
    startEditing,
    cancelEditing,
    reset: resetSubmission,
  } = useFormSubmissionState();

  const filledFilePoll = useFilledFilePoll();

  // A multi-step CTA's overview already found the guest's latest response (for any form, not only
  // one-response forms), so show that as the recap when the form itself loads blank.
  const sequenceRecap = sequence?.recap ?? null;
  const handleBlankForm = useCallback(
    (initialFormData: Record<string, unknown>) => {
      setFormData(initialFormData);
      if (sequenceRecap) showRecap(sequenceRecap);
    },
    [sequenceRecap, showRecap]
  );

  const renderableForm = useRenderableForm({
    isOpen,
    formId,
    landingPageId: landingPageId || null,
    viewer: prefillViewer,
    supabase,
    onSubmittedResponse: showRecap,
    onBlankForm: handleBlankForm,
  });
  const {
    formFields,
    formName,
    formDescription,
    confirmationMessage,
    emailTemplates,
    hasFileTemplate,
    willEmailFile,
    paymentEnabled,
    paymentAmountDisplay,
    customLogoUrl,
    isLoading,
    error,
    isFormLocked,
    isFormClosed,
    closedMessage,
    isRedirecting,
    markClosed,
    setCustomLogoUrl,
    resetForm,
  } = renderableForm;

  const {
    hasPayment,
    showCheckout,
    paymentClientSecret,
    paymentResponseId,
    paymentEmail,
    resolvedAmountDisplay,
    paymentCleanedFormData,
    preparePayment,
    closeCheckout,
  } = useFormPayment(paymentEnabled);

  const handleDraftRestored = useCallback((draftData: Record<string, unknown>, page: number) => {
    // Store pending draft - will be merged with initialized form data after form fields load
    setPendingDraft({ data: draftData, page });
  }, []);

  const { isSaving, lastSaved, clearDraft, isAuthenticated } = useFormAutoSave({
    formId,
    eventId,
    landingPageId: landingPageId || null,
    formData,
    formFields,
    currentPage,
    onDraftRestored: handleDraftRestored,
    // Only autosave while actively filling a fresh (not-yet-submitted) form —
    // editing an existing response has its own prefilled data, not a draft.
    disabled: submissionPhase !== "idle" || !isOpen || isLoading,
    // A loaded prior response means there's nothing to restore a stale local/DB
    // draft over — without this, a leftover draft could silently clobber the
    // freshly-fetched recap/edit data (mergeDraftOverFormData).
    hasExistingResponse: submissionRecap !== null,
  });

  // Reset to page 0 whenever the modal (re)opens for a form — mirrors
  // useRenderableForm's own load-on-open effect, kept separate since page
  // navigation is a presentation concern, not part of the loaded form's state.
  useEffect(() => {
    if (isOpen && formId) {
      setCurrentPage(0);
    }
  }, [isOpen, formId]);

  // Apply pending draft after form fields load
  useEffect(() => {
    if (!pendingDraft || isLoading || formFields.length === 0) return;

    // Merge so blank draft fields don't clobber viewer prefill (see helper docs).
    setFormData((prev) => mergeDraftOverFormData(prev, pendingDraft.data));
    setCurrentPage(pendingDraft.page);
    setPendingDraft(null);
  }, [pendingDraft, isLoading, formFields.length]);

  useEffect(() => {
    if (!isOpen || !formId) return;
    // A token on the specific button that opened this modal (e.g. a pricing card
    // linked to one tier) takes precedence over the page URL's own `?rt=` — opening
    // the modal doesn't navigate, so the page-level token is the only one otherwise
    // reachable here.
    const inviteToken = inviteTokenProp ?? searchParams?.get("rt") ?? null;
    // `viewer` is already resolved (server-side) by usePublicViewContext — its
    // non-null-ness alone implies a confirmed email, so it doubles as the
    // emailVerified signal here without a second auth/DB round trip.
    resolveRespondentTypeForViewer(formId, inviteToken, viewer?.email ?? null, !!viewer)
      .then(setRespondentTypeId)
      .catch((error) => console.error("FormModal: failed to resolve respondent type", error));
  }, [isOpen, formId, searchParams, viewer, inviteTokenProp]);

  const handleClose = () => {
    setFormData({});
    resetForm();
    resetSubmission();
    closeCheckout();
    setCurrentPage(0);
    setShowValidationErrors(false);
    setPendingDraft(null);
    hasMountedRef.current = false;
    onClose();
    // Scroll restoration is owned by FetchModal's scroll-lock cleanup; doing it
    // here too snapshots scrollY *after* the body is pinned (so it reads 0) and
    // overrides FetchModal's correct restore, jumping the page to the top.
  };

  const handleChange = (label: string, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [label]: value }));
  };

  /** Clears the saved draft and resets the on-screen form back to its initial (prefilled) state */
  const handleClearDraft = useCallback(async () => {
    await clearDraft();
    const initial = initializeFormData(formFields);
    setFormData(prefillFormDataFromViewer(initial, formFields, prefillViewer));
    setCurrentPage(0);
    setShowValidationErrors(false);
  }, [clearDraft, formFields, prefillViewer]);

  const handleFileUpload = async (field: FormField, file: File) => {
    try {
      const filePath = await uploadFile(field, file, formData[field.label], null);
      if (filePath) {
        setFormData((prev) => ({ ...prev, [field.label]: filePath }));
      }
    } catch (err) {
      console.error("FormModal: error uploading file", err);
      alert("Failed to upload file. Please try again.");
    }
  };

  // Derived values — visible fields with conditional logic
  const visibleFields = useMemo(
    () => computeVisibleFields(formFields, formData, respondentTypeId),
    [formFields, formData, respondentTypeId]
  );

  // Derived values — pagination
  const totalPages = Math.max(1, Math.ceil(visibleFields.length / FORM_FIELDS_PER_PAGE));
  const isLastPage = currentPage >= totalPages - 1;
  const pageFields = useMemo(
    () => visibleFields.slice(currentPage * FORM_FIELDS_PER_PAGE, (currentPage + 1) * FORM_FIELDS_PER_PAGE),
    [visibleFields, currentPage]
  );

  // Clamp currentPage if visibleFields shrinks (conditional logic hides fields)
  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(totalPages - 1);
    }
  }, [currentPage, totalPages]);

  // Set of field ids on the current page that are required and unfilled — used to draw red rings
  const invalidFieldIds = useMemo(() => {
    if (!showValidationErrors) return new Set<string>();
    const { invalidFields } = validateFormSubmission(pageFields, formData);
    return new Set(invalidFields.map((f) => f.id));
  }, [showValidationErrors, pageFields, formData]);

  // Memoized title node so typing in the form doesn't rebuild the modal header subtree.
  // The header shows only the logo; the form name lives in the body (as the form
  // heading while filling, and as an eyebrow above "Thank you!" once submitted).
  const modalTitle = useMemo(() => {
    if (showCheckout) return "Payment";
    if (isLoading) return "";
    if (!customLogoUrl) return "";
    return (
      // eslint-disable-next-line @next/next/no-img-element -- Logo URL is dynamic from Supabase storage
      <img
        src={customLogoUrl}
        alt=""
        className="max-h-6 w-auto max-w-full object-contain"
        onError={() => setCustomLogoUrl(null)}
      />
    );
  }, [showCheckout, isLoading, customLogoUrl, setCustomLogoUrl]);

  // Scroll to top of form on page transitions (skip initial render to avoid jolting the modal on open)
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    formScrollRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [currentPage]);

  /** Builds a summary of the user's form responses for the checkout step */
  const formSummary: FormSummaryEntry[] = useMemo(() => {
    return visibleFields
      .filter((f) => formData[f.label] && f.type !== "section" && f.type !== "description")
      .map((f) => {
        const raw = formData[f.label];
        let value: string;
        if (raw && typeof raw === "object" && "phone" in raw) {
          const phoneObj = raw as { phone: string; countryDialCodeId: number | null };
          const country = phoneObj.countryDialCodeId ? getCountryDialCodeById(phoneObj.countryDialCodeId) : null;
          const dialCode = country ? `+${String(country.dial_code)}` : "";
          value = dialCode ? `${dialCode} ${phoneObj.phone}` : phoneObj.phone;
        } else {
          value = String(raw);
        }
        return { label: f.label, value };
      });
  }, [visibleFields, formData]);

  /**
   * Sends this form's confirmation email for the given (already-cleaned) form data. Never throws.
   * `responseId` is the response the submit just created, so the email's edit link opens that
   * response instead of falling back to the email-only `?e=` link (which loads a blank form).
   */
  const sendConfirmationEmail = useCallback(
    (data: Record<string, unknown>, responseId: number | null) => {
      sendFormConfirmationEmail({
        formId,
        eventId,
        formData: data,
        formFields,
        emailTemplates,
        source: "LandingPageV2FormModal",
        responseId,
      });
    },
    [formId, eventId, formFields, emailTemplates]
  );

  const {
    onContinueFromRecap,
    showStepLabel,
    paginationLabel,
    dialogLabelSuffix,
    onEditSaved,
    endsSequenceNow,
    buildHandoffContact,
  } = useFormStepSequence({
    sequence,
    submissionPhase,
    submissionRecap,
    formFields,
    visibleFields,
    formData,
    emailTemplates,
    respondentTypeId,
    showToast,
  });
  // In a combined CTA the booking is this form's last page: the form's last "Next" hands over
  // instead of saving, unless the answers end the steps here (e.g. staff), which submit as usual.
  const defersSubmit = !!sequence?.deferSubmit && submissionPhase === "idle" && !hasPayment && !endsSequenceNow;
  const displayTotalPages = totalPages + (defersSubmit ? 1 : 0);

  const { isSubmitting, setIsSubmitting, submitForm, submit } = useFormSubmitFlow({
    formId,
    eventId,
    landingPageId: landingPageId || null,
    respondentTypeId,
    formData,
    formFields,
    visibleFields,
    emailTemplates,
    viewerEmail: viewer?.email ?? null,
    submissionRecapEmail: submissionRecap?.email ?? null,
    isEditingSubmission: submissionPhase === "editing",
    clearDraft,
    sendConfirmationEmail,
    markSubmitted,
    showRecap,
    showToast,
    onFilledFile: filledFilePoll.startPolling,
    editTarget: sequence?.editTarget ?? null,
    onEditSaved,
    onFormClosed: markClosed,
  });

  /**
   * Scrolls the topmost unfilled required field on the current page to the top of
   * the form. For text-style fields we also move focus into the input. Choice
   * fields (multiple choice / multi-select) signal invalidity via their red
   * question text alone, so we don't yank focus onto an individual radio/checkbox.
   */
  const focusFirstInvalidField = (firstInvalid: FormField) => {
    requestAnimationFrame(() => {
      const root = formScrollRef.current ?? document;
      const wrapper = root.querySelector<HTMLElement>(`[data-field-id="${firstInvalid.id}"]`);
      if (!wrapper) return;
      wrapper.scrollIntoView({ behavior: "smooth", block: "start" });
      if (firstInvalid.type === "multiplechoice" || firstInvalid.type === "multiselect") return;
      const focusable = wrapper.querySelector<HTMLElement>(
        "input:not([type='hidden']), select, textarea, [role='combobox'], [tabindex]:not([tabindex='-1'])"
      );
      focusable?.focus({ preventScroll: true });
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Advance to next page on intermediate pages — validate just the current page
    if (!isLastPage) {
      const pageValidation = validateFormSubmission(pageFields, formData);
      if (!pageValidation.isValid) {
        setShowValidationErrors(true);
        focusFirstInvalidField(pageValidation.invalidFields[0]);
        return;
      }
      setShowValidationErrors(false);
      setCurrentPage((p) => p + 1);
      return;
    }

    // Final submit — validate the entire form (jumps to first invalid page if any)
    const validation = validateFormSubmission(visibleFields, formData);
    if (!validation.isValid) {
      const firstInvalid = validation.invalidFields[0];
      const idx = visibleFields.findIndex((f) => f.id === firstInvalid.id);
      const targetPage = idx >= 0 ? Math.floor(idx / FORM_FIELDS_PER_PAGE) : currentPage;
      if (targetPage !== currentPage) {
        setCurrentPage(targetPage);
      }
      setShowValidationErrors(true);
      focusFirstInvalidField(firstInvalid);
      return;
    }

    // A combined CTA's booking follows as the form's last page: hand over instead of saving, and
    // the booking's one Submit saves both.
    if (defersSubmit && sequence?.deferSubmit) {
      setShowValidationErrors(false);
      sequence.deferSubmit({ contact: buildHandoffContact(), pageCount: totalPages, formName, submit });
      return;
    }

    // If payment enabled: submit the response FIRST (status: submitted, payment_status:
    // pending) and create its PaymentIntent, THEN show the checkout step — PaymentElement
    // needs a real clientSecret to mount, so there's no card form to show beforehand.
    // (Editing a payment-enabled submission isn't supported — the Edit CTA is hidden
    // for those forms, so this branch never runs mid-edit.)
    if (hasPayment) {
      setIsSubmitting(true);
      try {
        await preparePayment(
          // Confirmation email is deferred — sending it now would promise a confirmed
          // registration before any charge has actually gone through.
          () => submitForm({ sendConfirmation: false }),
          async (cleanedFormData, responseId) => {
            // Nothing to charge (free tier resolved) — send the confirmation email now,
            // since there's no payment step left to defer it to.
            sendConfirmationEmail(cleanedFormData, responseId);
            await clearDraft();
            invalidateFormCache(formId, landingPageId || null);
            markSubmitted();
          }
        );
      } catch (err) {
        console.error("FormModal: error preparing payment", err);
        showToast("Failed to prepare payment. Please try again.", "error");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // No payment — submit directly
    await submit();
  };

  const handleBack = () => {
    setShowValidationErrors(false);
    setCurrentPage((p) => Math.max(0, p - 1));
  };

  /** Allows jumping back to a previously visited step via the progress dots */
  const handleStepClick = (target: number) => {
    if (target >= currentPage) return;
    setShowValidationErrors(false);
    setCurrentPage(target);
  };

  /** Swaps the recap view into the (already-fetched) prefilled form for editing. */
  const handleStartEdit = useCallback(() => {
    if (!submissionRecap) return;
    // Overlay the saved answers on top of a freshly-initialized form (not a bare
    // assignment) so a field added since the original submission still gets its
    // default — matches the standalone form page's edit-prefill behavior.
    const initial = initializeFormData(formFields);
    setFormData(mergeSavedResponseOverDefaults(formFields, initial, submissionRecap.answers));
    setCurrentPage(0);
    setShowValidationErrors(false);
    startEditing();
  }, [submissionRecap, formFields, startEditing]);

  /** Starts a fresh response from the recap, for forms that accept more than one per person. */
  const handleSubmitAgain = useCallback(() => {
    const initial = initializeFormData(formFields);
    setFormData(prefillFormDataFromViewer(initial, formFields, prefillViewer));
    setCurrentPage(0);
    setShowValidationErrors(false);
    resetSubmission();
  }, [formFields, prefillViewer, resetSubmission]);

  /** Discards any in-progress edits and returns to the recap view without saving. */
  const handleCancelEdit = useCallback(() => {
    setShowValidationErrors(false);
    cancelEditing();
  }, [cancelEditing]);

  // Every phase swap (fill → recap, recap → edit, edit → recap, submit → thank-you)
  // replaces the visible content entirely. Without moving focus, the browser drops
  // it to <body>, which un-traps FetchModal's Tab handling (it only redirects Tab
  // when the first/last focusable node is active) and gives screen reader users no
  // signal the view changed. Focus the shared content container after each swap.
  const phaseContentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isLoading || isRedirecting) return;
    phaseContentRef.current?.focus();
  }, [isLoading, isRedirecting, submissionPhase, showCheckout, isFormLocked, isFormClosed, error]);

  // Render
  // Filling a fresh form and editing an existing submission share the same wide
  // form shell; the thank-you and recap states hold little content and shrink
  // the modal instead (auto height, narrow width, no full-screen takeover).
  const isFormPhase = submissionPhase === "idle" || submissionPhase === "editing";
  // A closed form shows only its closed message, so it takes the compact shell too.
  const isCompactModalState = (!isFormPhase && !showCheckout) || isFormClosed;
  const submitButtonLabel = isSubmitting
    ? submissionPhase === "editing"
      ? "Saving changes..."
      : "Submitting..."
    : !isLastPage || hasPayment || defersSubmit
      ? "Next"
      : submissionPhase === "editing"
        ? "Save changes"
        : "Submit";
  const content = (
    <FormModalErrorBoundary onClose={handleClose}>
      {isRedirecting && (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
          <p className="text-sm font-medium text-gray-600">Redirecting to sign in...</p>
        </div>
      )}

      {isLoading && !isRedirecting && (
        <div className="flex items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      )}

      {error && (
        <div className="py-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Announces the already-submitted recap regardless of how it was reached
            (initial detection or a 409 on resubmit) — the visible content swap alone
            gives screen reader users no signal. Same for the closed state (on load or
            a 410 on submit). */}
      <div role="status" aria-live="polite" className="sr-only">
        {submissionPhase === "previously-submitted"
          ? "You already submitted this form. Showing your submission."
          : closedMessage
            ? `This form is closed. ${closedMessage}`
            : ""}
      </div>

      {/* Shared focus target for every phase swap below — see the effect above. */}
      <div ref={phaseContentRef} tabIndex={-1} className="outline-none">
        {sequence && showStepLabel && !isLoading && !isRedirecting && <CtaStepLabel sequence={sequence} />}
        {/* Checkout Step */}
        {showCheckout &&
          submissionPhase !== "submitted" &&
          paymentClientSecret &&
          paymentResponseId &&
          paymentEmail && (
            <CheckoutStep
              clientSecret={paymentClientSecret}
              amountDisplay={resolvedAmountDisplay || paymentAmountDisplay}
              responseId={paymentResponseId}
              email={paymentEmail}
              primaryColor={primaryColor}
              onComplete={async () => {
                // Only now — payment_status confirmed "paid" server-side — is it true
                // that this registration is confirmed, so only now does the email go out.
                if (paymentCleanedFormData) {
                  sendConfirmationEmail(paymentCleanedFormData, paymentResponseId);
                }
                await clearDraft();
                closeCheckout();
                invalidateFormCache(formId, landingPageId || null);
                markSubmitted();
              }}
              onBack={closeCheckout}
              orderContext={orderContext}
              formSummary={formSummary}
            />
          )}

        {/* Form (fresh fill or editing an existing submission) */}
        {!isLoading && !error && !isFormClosed && visibleFields.length > 0 && isFormPhase && !showCheckout && (
          <div ref={formScrollRef}>
            {displayTotalPages > 1 && (
              <FormPageIndicator
                currentPage={currentPage}
                totalPages={displayTotalPages}
                primaryColor={primaryColor}
                label={paginationLabel}
                onPageClick={handleStepClick}
              />
            )}

            {/* Visually-hidden status region announces validation failures to screen readers */}
            <div role="alert" aria-live="assertive" className="sr-only">
              {showValidationErrors && invalidFieldIds.size > 0
                ? `Please complete ${invalidFieldIds.size} required ${invalidFieldIds.size === 1 ? "field" : "fields"}.`
                : ""}
            </div>

            {currentPage === 0 && (
              <>
                {formName && <h2 className="mb-2 text-left text-2xl font-bold text-gray-900">{formName}</h2>}
                <FormDescriptionContent html={formDescription} className="mb-6 text-left font-medium text-gray-600" />
              </>
            )}

            <form
              className="space-y-4 [&_input]:text-base [&_select]:text-base [&_textarea]:text-base"
              onSubmit={handleSubmit}
              aria-busy={isSubmitting}
              noValidate
            >
              {pageFields.map((field) => {
                const isInvalid = invalidFieldIds.has(field.id);
                // Text-like controls show their own red border (see FormFieldRenderer); only ring the
                // wrapper for borderless controls so the ring hugs the control, not the whole field.
                const showFieldRing = isInvalid && !FIELD_TYPES_WITH_INLINE_ERROR_BORDER.has(field.type);
                const dateBounds = field.type === "date" ? getDateFieldBounds(field, formFields, formData) : {};
                return (
                  <div key={field.id} data-field-id={field.id} className="mb-4">
                    <label
                      className={`mb-2 block text-left font-medium ${isInvalid ? "text-red-500" : "text-gray-700"}`}
                    >
                      <HtmlLabel html={field.label} /> {field.is_required && <span className="text-red-500">*</span>}
                    </label>
                    {field.description && <HtmlDescription html={field.description} />}
                    <div className={showFieldRing ? "rounded-lg ring-2 ring-red-500" : ""}>
                      <FormFieldRenderer
                        field={field}
                        value={formData[field.label]}
                        onChange={(value) => handleChange(field.label, value)}
                        onFileUpload={(file) => handleFileUpload(field, file)}
                        isUploading={uploadingFields[field.label]}
                        showValidationErrors={showValidationErrors}
                        hideDescription
                        dateMin={dateBounds.min}
                        dateMax={dateBounds.max}
                      />
                      <FieldComment
                        field={field}
                        value={formData[commentKeyForLabel(field.label)]}
                        onChange={(v) => handleChange(commentKeyForLabel(field.label), v)}
                      />
                    </div>
                  </div>
                );
              })}

              <div className="flex flex-wrap items-center justify-between gap-4">
                {submissionPhase !== "editing" && (
                  <FormSaveIndicator
                    isSaving={isSaving}
                    lastSaved={lastSaved}
                    themeColor={primaryColor}
                    isLocalOnly={!isAuthenticated}
                    onClearDraft={handleClearDraft}
                  />
                )}
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {currentPage > 0 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={isSubmitting}
                      className="touch-manipulation rounded-lg border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-all [@media(hover:hover)]:hover:bg-gray-50 disabled:opacity-50 sm:px-12"
                    >
                      Back
                    </button>
                  )}
                  {submissionPhase === "editing" && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isSubmitting}
                      className="touch-manipulation rounded-lg border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-all [@media(hover:hover)]:hover:bg-gray-50 disabled:opacity-50 sm:px-12"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={ACCENT_BUTTON_CLASS}
                    style={getAccentButtonStyle(primaryColor)}
                  >
                    {submitButtonLabel}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Success */}
        {submissionPhase === "submitted" && !showCheckout && (
          <FormThankYou
            formName={formName}
            confirmationMessage={confirmationMessage}
            hasFileTemplate={hasFileTemplate}
            willEmailFile={willEmailFile}
            filledDoc={{
              status: filledFilePoll.status,
              pdfUrl: filledFilePoll.pdfUrl,
              docxUrl: filledFilePoll.docxUrl,
            }}
            primaryColor={primaryColor}
            buttonTextColor={buttonTextColor}
          />
        )}

        {/* Already submitted (single-submission form the viewer already completed) */}
        {submissionPhase === "previously-submitted" && submissionRecap && !showCheckout && (
          <FormAlreadySubmitted
            // Inside the steps dialog its title already names the form.
            formName={embedded ? null : formName}
            recap={submissionRecap}
            formFields={formFields}
            canEdit={viewer !== null && !hasPayment}
            onEdit={handleStartEdit}
            onContinue={onContinueFromRecap}
            onSubmitAgain={sequence?.origin === "overview" && sequence.allowsResubmit ? handleSubmitAgain : undefined}
            primaryColor={primaryColor}
            buttonTextColor={buttonTextColor}
          />
        )}

        {/* Closed (manual toggle or passed closes_at): the admin's message instead of the fields */}
        {isFormClosed && (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            {/* Inside the steps dialog its title already names the form. */}
            {formName && !embedded && (
              <span
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: getReadableTextColor(primaryColor) }}
              >
                {formName}
              </span>
            )}
            <h3 className="text-2xl font-bold text-gray-900">Form closed</h3>
            <p className="max-w-prose whitespace-pre-line break-words text-sm font-medium text-gray-600">
              {closedMessage}
            </p>
          </div>
        )}

        {/* Locked */}
        {isFormLocked && (
          <div className="py-8 text-center">
            <p className="text-gray-600">Form has not been unlocked yet.</p>
          </div>
        )}
      </div>
    </FormModalErrorBoundary>
  );

  if (embedded) return content;

  return (
    <FetchModal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      ariaLabel={`${showCheckout ? "Payment" : formName || "Form"}${dialogLabelSuffix}`}
      size="auto"
      className={isCompactModalState ? PUBLIC_MODAL_COMPACT_CLASSNAME : PUBLIC_MODAL_FULL_SCREEN_MOBILE_CLASSNAME}
      contentClassName="overflow-y-auto overscroll-contain px-1"
    >
      {content}
    </FetchModal>
  );
}
